import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import orderBy from 'lodash/orderBy';
import { Box, Stack, Button } from '@mui/material';
import TicketCategory from '@/templates/plus/sections/tickets/TicketCategory';
import TicketTypes from '@/templates/plus/sections/tickets/TicketTypes';
import TicketCountSelect from '@/templates/plus/sections/tickets/TicketCountSelect';
import useTicketsContext from '@/contexts/tickets/useTicketsContext';
import useEventContext from '@/contexts/event/useEventContext';
import { extractData } from '@/store/ticket-types/useTicketTypes';
import useMobile from '@/hooks/useMobile';
import PromotionButton from '@/sections/promotion/PromotionButton';
import PromotionBanner from '@/sections/promotion/PromotionBanner';
import { useTicketsSelected } from '@/store';
import Iconify from '@/components/Iconify';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import MsiBanner from '@/sections/msi/MsiBanner';

function TicketsSelector({ onTicketCategorySelected }) {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const { onTicketSelectedFromList, onTicketTypeSelected, clearSelection } =
    useTicketsContext();
  const [numberOfTicketsSelected, setNumberOfTicketsSelected] = useState(1);
  const [sort, setSort] = useState();
  const [show, setShow] = useState(false);
  const { hasTickets } = useTicketsSelected();
  const { orderByPrice, clearTickets } = useAnalyticsEvents();

  const {
    event: {
      event: { max_tickets_per_booking, use_seats },
    },
    ticketTypes: ticketsData,
  } = useEventContext();

  const { ticketTypes } = extractData(ticketsData);

  const onSortHandler = () => {
    if (sort) {
      setSort((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort('desc');
    }
    orderByPrice();
  };

  const onTicketSelectedHandler = async (ticket) => {
    const resp = await onTicketSelectedFromList({
      ticket,
      quantity: numberOfTicketsSelected,
    });
    if (resp) {
      onTicketCategorySelected(ticket);
    }
  };

  const onTicketTypeQuantityChangeHandler = (ticket, bookingQuantity) => {
    onTicketTypeSelected({
      ticket,
      quantity: bookingQuantity,
    });
  };

  const getOrderedTicket = () => {
    if (sort) {
      return orderBy(ticketTypes, ['max_price'], [sort]);
    }
    return ticketTypes;
  };

  const clearTicketsHandler = async () => {
    await clearSelection();
    clearTickets();
  };

  useEffect(() => {
    setShow(true);
  }, []);

  return (
    <Box>
      {isMobile && (
        <Box>
          <MsiBanner />
        </Box>
      )}
      <Stack
        spacing={isMobile ? 2 : 4}
        justifyContent="space-between"
        direction="row"
        sx={{
          mt: isMobile ? 0 : 2,
          mb: 2,
        }}
      >
        {show && use_seats && (
          <>
            <TicketCountSelect
              max={max_tickets_per_booking}
              value={numberOfTicketsSelected}
              onChange={(value) => {
                setNumberOfTicketsSelected(value);
              }}
            />
          </>
        )}
        {show && (
          <>
            {hasTickets && (
              <Button
                variant="outlined"
                startIcon={
                  <Iconify
                    width={14}
                    height={14}
                    icon={'mdi:ticket-confirmation-outline'}
                  />
                }
                sx={{
                  padding: '14px',
                  borderRadius: '24px',
                }}
                onClick={clearTicketsHandler}
              >
                {t('checkout.buttons.clearTickets')}
              </Button>
            )}
          </>
        )}
        {show && !use_seats && <PromotionButton rounded />}
      </Stack>
      {show && !use_seats && (
        <Box
          sx={{
            width: '100%',
            mt: 2,
          }}
        >
          <PromotionBanner />
        </Box>
      )}
      {use_seats &&
        getOrderedTicket().map((item) => {
          return (
            <TicketCategory
              key={item.ticket_type_id}
              ticket={item}
              onTicketSelected={onTicketSelectedHandler}
            />
          );
        })}
      {!use_seats &&
        getOrderedTicket().map((item) => {
          return (
            <TicketTypes
              key={item.ticket_type_id}
              ticket={item}
              onQuantityChange={onTicketTypeQuantityChangeHandler}
            />
          );
        })}
    </Box>
  );
}

export default TicketsSelector;
